import { useEffect } from 'react';
import { FieldPath } from 'react-hook-form';

import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { RHFCustomerSelect } from 'components/input/RHFCustomerSelect';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Grid } from 'components/layout';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { NormalizedLoad } from 'types/Load';

import { CustomerSectionFormProps } from './types';

export function CustomerSectionForm({
  formMethods,
  customers,
  setCustomers,
  isLoadingCustomers,
  setIsLoadingCustomers,
  originalSuggestionData,
  isCreateMode = true,
}: CustomerSectionFormProps): React.JSX.Element {
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = formMethods;

  const { tmsIntegrations } = useServiceFeatures();
  const { aiDefaultValues } = useExtendedFormContext();

  const watchedCustomer = watch('customer');
  const watchedAddress2 = watch('customer.addressLine2');

  useEffect(() => {
    if (watchedCustomer?.externalTMSID) {
      const selectedCustomer = customers?.find(
        (customer) => customer.externalTMSID === watchedCustomer?.externalTMSID
      );
      if (!selectedCustomer) {
        return;
      }

      Object.entries(selectedCustomer).forEach(([key, value]) => {
        setValue(`customer.${key}` as FieldPath<NormalizedLoad>, value, {
          shouldDirty: aiDefaultValues,
        });
      });
    }
  }, [watchedCustomer?.externalTMSID, customers, setValue, aiDefaultValues]);

  return (
    <>
      <RHFCustomerSelect
        required
        name={`customer.externalTMSID`}
        label='Name'
        control={control}
        errors={errors}
        customers={customers}
        setCustomers={setCustomers}
        isLoadingCustomers={isLoadingCustomers}
        setIsLoadingCustomers={setIsLoadingCustomers}
        tmsID={tmsIntegrations?.[0]?.id}
        disabled={!isCreateMode}
      />

      <RHFTextInput
        name='customer.addressLine1'
        label='Address Line 1'
        readOnly={true}
      />

      {watchedAddress2 && (
        <RHFTextInput
          name='customer.addressLine2'
          label='Address Line 2'
          readOnly={true}
        />
      )}
      <RHFTextInput name='customer.city' label='City' readOnly={true} />

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <RHFTextInput name='customer.state' label='State' readOnly={true} />
        <RHFTextInput
          name='customer.zipCode'
          label='Zip Code'
          placeholder='12345'
          readOnly={true}
        />
      </Grid>

      <LoadRefPillInput
        name='customer.refNumber'
        label='Ref #/ BOL'
        control={control}
        originalSuggestionData={originalSuggestionData || getValues()}
        autoSelectFirstOption={false}
      />

      <LoadRefPillInput
        name='poNums'
        label='PO #'
        control={control}
        originalSuggestionData={originalSuggestionData}
        autoSelectFirstOption={false}
        maxChars={25}
        rules={{
          validate: (value) => {
            const str = typeof value === 'string' ? value : '';
            if (str.length > 25) {
              return `PO # must be 25 characters or fewer (currently ${str.length})`;
            }
            return true;
          },
        }}
      />
    </>
  );
}
